import { useTranslation } from 'react-i18next';
import React from 'react';
import { notNil } from '../../../core/helpers/is-nil';
import { ORDER_STATUS_TRANSLATION_MAP } from '../../../domain/customer/types';
import { OpenOrderDto } from '../../../modules/backend/openapi/api-types';
import { createColumns } from '../../../modules/data-table/create-columns';
import { DataTable } from '../../../modules/data-table/data-table';
import { makeAccessorColumnBuilder } from '../../../modules/data-table/helpers';
import { GridContainer, GridItem } from '../../../visual-components/grid/grid';
import { TableLayout } from '../../../visual-components/table-layout/table-layout';
import { TableCellDateTime } from '../../../visual-components/table/table-cells/table-cell-date-time/table-cell-date-time';
import { TableCellIconLabel } from '../../../visual-components/table/table-cells/table-cell-icon-label/table-cell-icon-label';
import { Table } from '../../../visual-components/table/table/table';
import { QueryErrorHandler } from '../../../visual-components/query-error-handler/query-error-handler';

type Props = {
  openOrders: OpenOrderDto[];
  isLoading: boolean;
  hasError: boolean;
  error?: React.ComponentProps<typeof QueryErrorHandler>['error'];
  debugInfo?: React.ComponentProps<typeof QueryErrorHandler>['debugInfo'];
};

export const OpenOrders: React.FC<Props> = ({ openOrders, isLoading, hasError, error, debugInfo }) => {
  const { t } = useTranslation();
  const createAccessorColumn = makeAccessorColumnBuilder<OpenOrderDto>();

  const colDef = [
    createAccessorColumn('orderNo', {
      headerLabel: t('profile.openOrders.orderNumber'),
    }),
    createAccessorColumn('deliveryDate', {
      headerLabel: t('profile.openOrders.deliveryDate'),
      renderCell: ({ value }) => {
        if (notNil(value)) {
          return <TableCellDateTime date={new Date(value)} />;
        }
      },
    }),
    createAccessorColumn('transmitted', {
      headerLabel: t('profile.openOrders.transmitted'),
      renderCell: ({ value }) => {
        if (notNil(value)) {
          return <TableCellIconLabel variant="check" />;
        }
      },
    }),
    createAccessorColumn('itemCount', {
      headerLabel: t('profile.openOrders.itemCount'),
    }),
    createAccessorColumn('orderStatus', {
      headerLabel: t('profile.openOrders.status'),
      renderCell: ({ value }) => ORDER_STATUS_TRANSLATION_MAP[value](t),
    }),
  ];

  return (
    <GridContainer>
      <GridItem span="12">
        <DataTable
          data={openOrders}
          hasError={hasError}
          isLoading={isLoading}
          columns={createColumns({
            colDef,
            rowIdentifierColumnId: 'orderNo',
            lightHeader: true,
          })}
        >
          {(props) => (
            <TableLayout
              debugInfo={debugInfo}
              error={error}
              table={<Table {...props} label="openOrders" />}
              title={t('profile.openOrders.title')}
            />
          )}
        </DataTable>
      </GridItem>
    </GridContainer>
  );
};
